import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Divider, 
  Paper, 
  CircularProgress,
  Button
} from '@mui/material';
import { getUserChats } from '../services/chats';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';

const UserChatsList = ({ userId, onChatSelect }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchChats = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError('');
      
      try {
        const fetchedChats = await getUserChats(userId);
        setChats(fetchedChats);
      } catch (err) {
        console.error('Error fetching chats:', err);
        setError('Failed to load chats. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    
    fetchChats();
  }, [userId]);
  
  // Format date in a user-friendly way
  const formatDate = (date) => {
    if (!date) return 'Unknown';
    
    const now = new Date();
    const chatDate = new Date(date);
    
    // If date is today, show only time
    if (chatDate.toDateString() === now.toDateString()) {
      return chatDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // If date is this year, show month and day
    if (chatDate.getFullYear() === now.getFullYear()) {
      return chatDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    
    // Otherwise show full date
    return chatDate.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="subtitle1">Error loading chats</Typography>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (chats.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <MessageIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography variant="subtitle1" color="text.secondary">
          This user has no chats yet
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">
          User Chats ({chats.length})
        </Typography>
      </Box>
      
      <List sx={{ maxHeight: '500px', overflow: 'auto' }}>
        {chats.map((chat, index) => (
          <React.Fragment key={chat.id}>
            <ListItem 
              alignItems="flex-start" 
              button
              onClick={() => onChatSelect(chat.id)}
              sx={{ 
                '&:hover': { 
                  bgcolor: 'action.hover' 
                }
              }}
            >
              <ListItemAvatar>
                <Avatar 
                  src={chat.otherUser.avatar} 
                  alt={chat.otherUser.name}
                >
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      component="span" 
                      variant="subtitle1" 
                      fontWeight="medium"
                    >
                      {chat.otherUser.name || 'Unknown'}
                    </Typography>
                    <Typography 
                      component="span" 
                      variant="caption" 
                      color="text.secondary"
                    >
                      {chat.updated ? formatDate(chat.updated) : 'No date'}
                    </Typography>
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                      sx={{
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {chat.lastMessage || 'No messages'}
                    </Typography>
                    <Typography 
                      component="div" 
                      variant="caption" 
                      color="text.secondary"
                    >
                      @{chat.otherUser.userName || 'Unknown'}
                    </Typography>
                  </React.Fragment>
                }
              />
            </ListItem>
            {index < chats.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default UserChatsList;